import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { filter } from 'rxjs';
import {
  KcIconComponent,
  KcIconButtonComponent,
  KcSidebarContainerComponent,
  KcSidebarComponent,
  LeftSidebarService,
  ResponsiveService,
} from '@kaspacom/ui-kit';
import { CookieConsentBannerComponent } from './components/cookie-consent-banner/cookie-consent-banner.component';
import { CookieConsentModalComponent } from './components/cookie-consent-modal/cookie-consent-modal.component';
import { DefiLeftSidebarComponent } from './components/defi-left-sidebar/defi-left-sidebar.component';
import { DefiNavBarComponent } from './components/defi-nav-bar/defi-nav-bar.component';
import { KaspaComWebWalletIframeComponent } from './components/kaspa-com-web-wallet-iframe/kaspa-com-web-wallet-iframe.component';
import { SnackbarTopLayerComponent } from './components/snackbar-top-layer/snackbar-top-layer.component';
import { WalletConnectComponent } from './components/wallet-connect/wallet-connect.component';
import { WalletOperationTaskOverlayComponent } from './components/wallet-operation-task-overlay/wallet-operation-task-overlay.component';
import { WalletManager } from './managers/wallet.manager';
import { Kcc20ReferralAttributionService } from './services/referrals/kcc20-referral-attribution.service';
import { ThemeService } from './services/theme.service';

interface MobileNavItem {
  readonly label: string;
  readonly path: string;
  readonly iconClass: string;
  readonly activePrefixes: readonly string[];
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    CookieConsentBannerComponent,
    CookieConsentModalComponent,
    DefiLeftSidebarComponent,
    DefiNavBarComponent,
    KcIconComponent,
    KcIconButtonComponent,
    KcSidebarComponent,
    KcSidebarContainerComponent,
    SnackbarTopLayerComponent,
    WalletConnectComponent,
    KaspaComWebWalletIframeComponent,
    WalletOperationTaskOverlayComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly walletManager = inject(WalletManager);
  // Eagerly construct ThemeService so the saved/system theme is applied on app
  // startup. It is otherwise only instantiated by the desktop theme-toggle,
  // which isn't rendered on mobile — leaving mobile stuck on the default dark.
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly responsiveService = inject(ResponsiveService);
  private readonly leftSidebarService = inject(LeftSidebarService);
  private readonly referralAttribution = inject(
    Kcc20ReferralAttributionService,
  );

  protected readonly leftSidebarOpened = this.leftSidebarService.isOpen;
  protected readonly currentUrl = signal(this.router.url);
  protected readonly mobileNavItems: readonly MobileNavItem[] = [
    {
      label: 'Markets',
      path: '/markets',
      iconClass: 'analytics',
      activePrefixes: ['/markets', '/tokens'],
    },
    {
      label: 'Mints',
      path: '/mints',
      iconClass: 'minde',
      activePrefixes: ['/mints'],
    },
    {
      label: 'Portfolio',
      path: '/portfolio',
      iconClass: 'coins-02',
      activePrefixes: ['/portfolio'],
    },
    {
      label: 'Rewards',
      path: '/rewards',
      iconClass: 'gift',
      activePrefixes: ['/rewards'],
    },
    {
      label: 'Deploy',
      path: '/deploy-token',
      iconClass: 'rocket',
      activePrefixes: ['/deploy-token', '/import-token'],
    },
  ];
  protected readonly isMobile = computed(
    () => this.responsiveService.deviceType() !== 'desktop',
  );
  protected readonly sidebarWidth = computed(() => (this.isMobile() ? 0 : 240));
  protected readonly sidebarMinWidth = computed(() =>
    this.isMobile() ? 0 : 84,
  );

  constructor() {
    this.referralAttribution.start();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        // #route-content (not the document) is the app's real scroll
        // container (see app.component.scss) — the router's own
        // scrollPositionRestoration only targets window scroll, so it can't
        // reset this on its own. Compare paths (ignoring query string) so a
        // same-page query-param navigation (e.g. a tab or filter change)
        // doesn't yank the user back to the top of the page they're on.
        const previousPath = this.currentUrl().split('?')[0];
        const nextPath = event.urlAfterRedirects.split('?')[0];
        this.currentUrl.set(event.urlAfterRedirects);
        if (previousPath !== nextPath) {
          document.getElementById('route-content')?.scrollTo({ top: 0 });
        }
      });
  }

  protected toggleLeftSidebar(): void {
    this.leftSidebarService.toggle();
  }

  protected setLeftSidebarOpen(isOpen: boolean): void {
    this.leftSidebarService.setOpen(isOpen);
  }

  protected isMobileNavActive(item: MobileNavItem): boolean {
    const currentUrl = this.currentUrl();

    return item.activePrefixes.some(
      (prefix) => currentUrl === prefix || currentUrl.startsWith(`${prefix}/`),
    );
  }
}
